import { useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';
import LLMApiService from '@minohealth/common/services/LLMApiService';
import { showErrorMessage } from '@minohealth/common/utils/notifiers';
import useSharedEffect from './useSharedEffect';

const integrationsAtom = atom([]);
const integrationsLoadingAtom = atom(true);
const justCompletedAtom = atom(false);
const justErroredAtom = atom(false);
const integrationHealthAtom = atom({});

const POLLING_KEY = Symbol('integrations-polling');

export function usePendingIntegrations({ pollingInterval = 30000 } = {}) {
  const { user } = useSelector(state => state.app.datastate);
  const apiService = useMemo(() => LLMApiService(user?.userToken), [user?.userToken]);
  
  const [integrations, setIntegrations] = useAtom(integrationsAtom);
  const [isLoading, setIsLoading] = useAtom(integrationsLoadingAtom);
  const [justCompleted, setJustCompleted] = useAtom(justCompletedAtom);
  const [justErrored, setJustErrored] = useAtom(justErroredAtom);
  
  const previousPendingCountRef = useRef(0);
  const previousErrorCountRef = useRef(0);

  const fetchIntegrations = useCallback(async () => {
    if (!user?.userToken) return;
    
    try {
      const { data } = await apiService.getIntegrations();
      const list = Array.isArray(data) ? data : [];
      
      const newPendingCount = list.filter(i => i.status === 'pending').length;
      const newErrorCount = list.filter(i => i.status === 'error').length;
      const previousCount = previousPendingCountRef.current;
      const previousErrorCount = previousErrorCountRef.current;
      
      // Detect completion: previous count was higher (integration finished)
      if (previousCount > 0 && newPendingCount < previousCount) {
        setJustCompleted(true);
      }
      
      // Detect new error
      if (newErrorCount > previousErrorCount) {
        setJustErrored(true);
      }
      
      previousPendingCountRef.current = newPendingCount;
      previousErrorCountRef.current = newErrorCount;
      setIntegrations(list);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.userToken, apiService]);

  const pendingIntegrations = integrations.filter(i => i.status === 'pending');
  const errorIntegrations = integrations.filter(i => i.status === 'error');
  const hasPending = pendingIntegrations.length > 0;

  useSharedEffect(POLLING_KEY, () => {
    fetchIntegrations();
    
    const intervalId = setInterval(() => {
      // Only poll if there are pending integrations
      if (hasPending) {
        fetchIntegrations();
      }
    }, pollingInterval);
    
    return () => clearInterval(intervalId);
  }, [fetchIntegrations, pollingInterval, hasPending]);
  const hasErrors = errorIntegrations.length > 0;
  const pendingCount = pendingIntegrations.length;
  const errorCount = errorIntegrations.length;

  // Toggle mutation with optimistic updates
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }) => apiService.toggleIntegrationStatus(id, enabled),
    onMutate: async ({ id, enabled }) => {
      // Optimistic update
      setIntegrations(prev =>
        prev.map(integ =>
          (integ._id === id || integ.uniqueId === id)
            ? { ...integ, enabled }
            : integ
        )
      );
      // Return previous state for rollback
      return { previousIntegrations: integrations };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousIntegrations) {
        setIntegrations(context.previousIntegrations);
      }
      showErrorMessage(error?.response?.data?.message ?? 'Failed to update integration.');
    },
    onSuccess: () => {
      // Refetch to sync with server
      fetchIntegrations();
    },
  });

  const [healthStatus, setHealthStatus] = useAtom(integrationHealthAtom);

  const healthCheckMutation = useMutation({
    mutationFn: (id) => apiService.checkIntegrationHealth(id),
    onSuccess: (data, id) => {
      setHealthStatus(prev => ({ ...prev, [id]: data }));
    },
    onError: (error, id) => {
      setHealthStatus(prev => ({ 
        ...prev, 
        [id]: { healthy: false, error: error?.response?.data?.message }
      }));
    },
    retry: (failureCount, error) => {
      if (error?.response?.data?.code === 'INTERNAL_SERVER_ERROR' && failureCount < 3) {
        return true;
      }
      return false;
    },
  });

  const checkAllHealth = useCallback(async () => {
    const successIds = integrations
      .filter(i => i.status === 'success')
      .map(i => i._id);
    
    if (successIds.length === 0) return;
    await Promise.all(successIds.map(id => healthCheckMutation.mutateAsync(id)));
  }, [integrations, healthCheckMutation]);

  return {
    hasPending,
    hasErrors,
    pendingCount,
    errorCount,
    isLoading,
    integrations,
    refetch: fetchIntegrations,
    justCompleted,
    justErrored,
    toggleIntegration: toggleMutation.mutate,
    healthStatus,
    checkAllHealth,
  };
}
